import { z } from 'zod';
import { apiRequest } from './core/client';
import { parseWithSchema } from './schemas';

/**
 * "Add to order journey" from the mail reader, after the orders app moved to
 * its own host (docs/ORDERS_EXTRACTION_PLAN_2026-09-05.md §3d).
 *
 * These two calls go to sundae's own server, which proxies them to the orders
 * API with ORDERS_SERVICE_TOKEN and an X-On-Behalf-Of header. The browser
 * never holds an orders token, and the request carries no account id: message
 * ids are globally unique and the orders app must never learn that accounts
 * exist. The orders side does grant + snapshot + blob copy + journey link in
 * one transaction, so a failure here means nothing was written anywhere.
 *
 * Distinct from api/orders.ts on purpose. That module talks to the orders
 * feature still living inside sundae and goes away at cutover; this one is the
 * boundary that survives it.
 */

/**
 * One journey in the picker. A strip-unknowns schema (zod's default) rather
 * than a mirror of the full orders list row: the orders app owns that shape,
 * and the reader only needs enough to name and rank a journey. Ids are
 * bigints, which Postgres hands back as strings — z.coerce.string() so a
 * numeric id in JSON does not become a silently-wrong number.
 */
export const OrderJourneyOptionSchema = z.object({
  id: z.coerce.string(),
  vendor: z.string(),
  label: z.string().nullable().optional().default(null),
  status: z.string(),
  placedDate: z.string().nullable().optional().default(null),
  orderDueDate: z.string().nullable().optional().default(null),
  archivedAt: z.string().nullable().optional().default(null),
  updatedAt: z.string().nullable().optional().default(null),
  receivingIds: z.array(z.coerce.string()).optional().default([]),
});

export type OrderJourneyOption = z.infer<typeof OrderJourneyOptionSchema>;

const OrderJourneyListSchema = z.object({
  orders: z.array(OrderJourneyOptionSchema),
});

/**
 * GET /api/emails/:id/order-journeys — open journeys, ranked and filtered by
 * the dialog. `q` is forwarded to the orders API; the reader does not use it
 * today (a few hundred journeys filter fine in the browser) but the route
 * takes it, so callers with a longer list do not have to add it later.
 */
export async function fetchOrderJourneys(
  messageId: string,
  options: { q?: string; signal?: AbortSignal } = {}
): Promise<OrderJourneyOption[]> {
  const data = await apiRequest<unknown>({
    path: `/emails/${encodeURIComponent(messageId)}/order-journeys`,
    method: 'GET',
    query: options.q ? { q: options.q } : undefined,
    signal: options.signal,
  });
  return parseWithSchema(OrderJourneyListSchema, data, 'fetchOrderJourneys').orders;
}

/** What linking the email MEANS to the journey. Mirrors the orders app's source kinds. */
export type OrderJourneySourceKind =
  | 'announcement'
  | 'invoice'
  | 'carrier_charge'
  | 'package'
  | 'order'
  | 'other';

const LinkResultSchema = z.object({
  order: z
    .object({
      id: z.coerce.string(),
      vendor: z.string().optional(),
      label: z.string().nullable().optional(),
    })
    .optional(),
  snapshot: z
    .object({
      message_id: z.coerce.string(),
    })
    .optional(),
});

export type OrderJourneyLinkResult = z.infer<typeof LinkResultSchema>;

/**
 * POST /api/emails/:id/order-journey — link this email to the journey.
 *
 * `kind` is what the email IS to that journey, which is what decides whether
 * the orders app reads it as the ANNOUNCED, INVOICED or carrier document. The
 * reader cannot know that, so it sends 'other' and lets the next orders run
 * classify it — the same "queue it for the run to read" behaviour the dialog
 * has had since it was a process_email nudge.
 */
export async function linkEmailToOrderJourney(
  messageId: string,
  input: { orderId: string; kind: OrderJourneySourceKind; note?: string | null }
): Promise<OrderJourneyLinkResult> {
  const data = await apiRequest<unknown>({
    path: `/emails/${encodeURIComponent(messageId)}/order-journey`,
    method: 'POST',
    body: {
      order_id: input.orderId,
      kind: input.kind,
      ...(input.note ? { note: input.note } : {}),
    },
  });
  return parseWithSchema(LinkResultSchema, data, 'linkEmailToOrderJourney');
}
